import React, { useState, useEffect, useCallback } from 'react';
import { RefreshIcon, AlertIcon } from '../../../components/common/Icons';
import { useVersionRegistry } from '../hooks/useVersionRegistry';
import { VersionResponse, VersionCreatePayload, VersionUpdatePayload } from '../types';
import VersionCard from './VersionCard';
import VersionFormModal from './VersionFormModal';
import VersionDetailsModal from './VersionDetailsModal';

const VersionRegistryPanel: React.FC = () => {
  const {
    versions, loading, saving, error,
    loadVersions, editVersion, deprecateVersion, clearError,
  } = useVersionRegistry();

  const [formOpen, setFormOpen] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState<VersionResponse | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsVersion, setDetailsVersion] = useState<VersionResponse | null>(null);
  const [deprecateTarget, setDeprecateTarget] = useState<VersionResponse | null>(null);

  useEffect(() => {
    loadVersions();
  }, [loadVersions]);

  const handleEdit = useCallback((v: VersionResponse) => {
    setSelectedVersion(v);
    clearError();
    setFormOpen(true);
  }, [clearError]);

  const handleViewDetails = useCallback((v: VersionResponse) => {
    setDetailsVersion(v);
    setDetailsOpen(true);
  }, []);

  const handleDeprecateClick = useCallback((v: VersionResponse) => {
    setDeprecateTarget(v);
  }, []);

  const handleFormSubmit = async (payload: VersionCreatePayload | VersionUpdatePayload) => {
    try {
      if (selectedVersion) {
        await editVersion(selectedVersion.version, payload);
      }
      setFormOpen(false);
    } catch {
      // Error displayed in modal via hook error state
    }
  };

  const handleDeprecateConfirm = async () => {
    if (!deprecateTarget) return;
    try {
      await deprecateVersion(deprecateTarget.version);
      setDeprecateTarget(null);
    } catch {
      // Error displayed via hook
    }
  };

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
        <h2>Version Registry</h2>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button className="btn btn-secondary btn-sm" onClick={loadVersions} disabled={loading}>
            <RefreshIcon size={14} /> Refresh
          </button>
        </div>
      </div>

      {/* Error */}
      {error && !formOpen && (
        <div className="alert alert-error" style={{ marginBottom: '16px' }}>
          <AlertIcon size={18} />
          <span>{error}</span>
          <button className="btn btn-sm" onClick={clearError}>Dismiss</button>
        </div>
      )}

      {/* Loading */}
      {loading && versions.length === 0 && (
        <div className="loading-container">
          <div className="loading-spinner" />
          <p>Loading versions...</p>
        </div>
      )}

      {/* Version list */}
      {versions.length > 0 && (
        <div>
          {versions.map((v) => (
            <VersionCard
              key={v.version}
              version={v}
              onViewDetails={handleViewDetails}
              onEdit={handleEdit}
              onDeprecate={handleDeprecateClick}
            />
          ))}
        </div>
      )}

      {/* Empty state */}
      {!loading && versions.length === 0 && !error && (
        <div className="empty-state">
          <p>No versions registered yet. Run the release pipeline to register a version.</p>
        </div>
      )}

      {/* Form Modal (edit only) */}
      <VersionFormModal
        isOpen={formOpen}
        mode="edit"
        version={selectedVersion}
        onClose={() => setFormOpen(false)}
        onSubmit={handleFormSubmit}
        saving={saving}
        error={formOpen ? error : null}
        onClearError={clearError}
      />

      {/* Details Modal */}
      <VersionDetailsModal
        isOpen={detailsOpen}
        version={detailsVersion}
        onClose={() => setDetailsOpen(false)}
      />

      {/* Deprecation Confirmation */}
      {deprecateTarget && (
        <div className="modal-overlay" onClick={() => setDeprecateTarget(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '440px' }}>
            <div className="modal-header">
              <h2>Deprecate Version</h2>
              <button className="btn btn-ghost" onClick={() => setDeprecateTarget(null)}>×</button>
            </div>
            <div className="modal-body">
              <p>
                Are you sure you want to deprecate version <strong>{deprecateTarget.version}</strong>?
              </p>
              <p style={{ fontSize: '13px', opacity: 0.7, marginTop: '8px' }}>
                Deprecated versions will no longer be available as upgrade targets. This action can be reversed by editing the version.
              </p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setDeprecateTarget(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleDeprecateConfirm} disabled={saving}>
                {saving ? 'Deprecating...' : 'Deprecate'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VersionRegistryPanel;
